import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import SolicitacaoAcessoModal from '../components/common/SolicitacaoAcessoModal';
import EsqueciSenhaModal from '../components/common/EsqueciSenhaModal';
import RedefinirSenhaModal from '../components/common/RedefinirSenhaModal';
import SelecaoFilialModal from '../components/common/SelecaoFilialModal';
import WhatsAppWidget from '../components/WhatsAppWidget';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setFilialAtualSemValidacao } = useApp();
  const [formData, setFormData] = useState({
    login: '',
    senha: '',
    captcha: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEsqueciSenhaOpen, setIsEsqueciSenhaOpen] = useState(false);
  const [isRedefinirSenhaOpen, setIsRedefinirSenhaOpen] = useState(false);
  const [isSelecaoFilialOpen, setIsSelecaoFilialOpen] = useState(false);
  const [usuarioParaRedefinir, setUsuarioParaRedefinir] = useState<{ id: string; nome: string; filiais: string[] } | null>(null);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaUrl, setCaptchaUrl] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando usuário digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.login) {
      newErrors.login = 'Usuário ou Login é obrigatório';
    }

    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    }

    if (requiresCaptcha && !formData.captcha) {
      newErrors.captcha = 'CAPTCHA é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadCaptcha = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/auth/captcha`, {
        headers: {
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        }
      });
      if (response.ok) {
        const svg = await response.text();
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        setCaptchaUrl(url);
      }
    } catch (error) {
      console.error('Erro ao carregar CAPTCHA:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const user = await login(formData.login, formData.senha, formData.captcha);

      // Verificar se é primeiro acesso
      if (user?.primeiro_acesso) {
        setUsuarioParaRedefinir({
          id: user.id,
          nome: user.nome,
          filiais: user.filiais || []
        });
        setIsRedefinirSenhaOpen(true);
        setLoading(false);
      } else {
        // Se usuário tem múltiplas filiais, abrir modal de seleção
        if (user?.filiais && user.filiais.length > 1) {
          setUsuarioParaRedefinir({
            id: user.id,
            nome: user.nome,
            filiais: user.filiais
          });
          setIsSelecaoFilialOpen(true);
          setLoading(false);
        } else {
          // Se tem apenas uma filial, usar ela e navegar
          if (user?.filiais && user.filiais.length > 0) {
            setFilialAtualSemValidacao(user.filiais[0]);
          }
          navigate('/inicio');
        }
      }

    } catch (error: any) {
      console.error('Erro no login:', error);

      // Verificar se o erro indica que CAPTCHA é necessário
      if (error.requiresCaptcha) {
        setRequiresCaptcha(true);
        loadCaptcha();
        setErrors({
          form: error.message || 'Após múltiplas tentativas falhas, é necessário resolver o CAPTCHA.'
        });
      } else {
        setErrors({
          form: error.message || 'Erro ao fazer login. Verifique suas credenciais.'
        });
      }
      setLoading(false);
    }
  };

  const handleFilialSelected = (filial: string) => {
    setFilialAtualSemValidacao(filial);
    navigate('/inicio');
  };

  const handleRedefinirSenhaSuccess = () => {
    // Após redefinir senha, fazer login novamente
    setIsRedefinirSenhaOpen(false);
    setUsuarioParaRedefinir(null);
    navigate('/inicio');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111827] py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-blue-900/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-blue-900/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="bg-white p-8 rounded-2xl shadow-2xl">
          <div className="text-center mb-8">
            <img 
              src="/falcon hub 1.png" 
              alt="Falcon Hub" 
              className="mx-auto h-32 w-auto mb-4"
            />
            <h2 className="mt-6 text-3xl font-bold text-gray-900 tracking-tight">
              Acesso ao Sistema
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-medium">
              Versão 1.0
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {errors.form && (
              <div className="rounded-xl bg-red-50 p-4 border border-red-100">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 font-medium">{errors.form}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="relative">
                <Input
                  label="Usuário ou Email"
                  type="text"
                  value={formData.login}
                  onChange={(value) => handleChange('login', value)}
                  error={errors.login}
                  placeholder="Seu usuário ou email"
                  required
                  dataTestId="email"
                />
              </div>

              <div className="relative">
                <Input
                  label="Senha"
                  type="password"
                  value={formData.senha}
                  onChange={(value) => handleChange('senha', value)}
                  error={errors.senha}
                  placeholder="&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;"
                  required
                  dataTestId="password"
                />
              </div>

              {requiresCaptcha && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    CAPTCHA
                  </label>
                  <div className="flex items-center gap-4">
                    {captchaUrl && (
                      <img 
                        src={captchaUrl} 
                        alt="CAPTCHA" 
                        className="border border-gray-300 rounded"
                        onClick={loadCaptcha}
                        style={{ cursor: 'pointer' }}
                        title="Clique para recarregar"
                      />
                    )}
                    <button
                      type="button"
                      onClick={loadCaptcha}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      🔄 Recarregar
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={formData.captcha}
                    onChange={(value) => handleChange('captcha', value)}
                    error={errors.captcha}
                    placeholder="Digite os caracteres do CAPTCHA"
                    required={requiresCaptcha}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
                  Lembrar-me
                </label>
              </div>

              <div className="text-sm space-x-4">
                <button
                  type="button"
                  onClick={() => setIsEsqueciSenhaOpen(true)}
                  className="font-semibold text-blue-600 hover:text-blue-500 transition-colors bg-transparent border-none p-0 cursor-pointer"
                  data-testid="esqueci-senha"
                >
                  Esqueceu a senha?
                </button>
                <Link
                  to="/politica-privacidade"
                  className="font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                  target="_blank"
                >
                  Política de Privacidade
                </Link>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transform active:scale-[0.98] transition-all font-bold text-base"
                loading={loading}
                disabled={loading}
                dataTestId="login-button"
              >
                {loading ? 'Verificando...' : 'Entrar no Sistema'}
              </Button>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-gray-500">
                Novo por aqui?{' '}
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="font-bold text-blue-600 hover:text-blue-500 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  Solicite seu acesso
                </button>
              </p>
            </div>

            {/* Botão de emergência para limpar bloqueios */}
            <div className="text-center pt-2 border-t border-gray-200 mt-4">
              <button
                type="button"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-none p-0 cursor-pointer"
              >
                🔄 Limpar sessão e tentar novamente
              </button>
            </div>
          </form>
        </div>
      </div>

      <SolicitacaoAcessoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <EsqueciSenhaModal
        isOpen={isEsqueciSenhaOpen}
        onClose={() => setIsEsqueciSenhaOpen(false)}
      />

      <RedefinirSenhaModal
        isOpen={isRedefinirSenhaOpen}
        onClose={() => {
          setIsRedefinirSenhaOpen(false);
          setUsuarioParaRedefinir(null);
        }}
        usuarioId={usuarioParaRedefinir?.id || ''}
        usuarioNome={usuarioParaRedefinir?.nome || ''}
        onSuccess={handleRedefinirSenhaSuccess}
      />

      <SelecaoFilialModal
        isOpen={isSelecaoFilialOpen}
        onClose={() => setIsSelecaoFilialOpen(false)}
        onFilialSelected={handleFilialSelected}
        filiaisDisponiveis={usuarioParaRedefinir?.filiais || []}
      />

      <WhatsAppWidget />
    </div>
  );
};

export default Login;
